import { ApplicationContext, Page } from '../App';
import logo from '../images/white-logo.png';
import { COLOR, isWebDeployment } from './shared/shared';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
	PalletizerContext,
	RuntimeContext,
} from '../context/PalletizerContext';
import {
	ARStatus,
	Application,
	PalletizerInformation,
	PalletizerStatus,
} from 'libapi';
import Button from './PendantButton';
import menu_icon from '../images/burger_menu.png';

import {
	getConfigValueFromKey,
	InformationLog,
	Scroll,
	useCoreIOConfig,
	useMachineConfig,
} from './General';
import PalletizerRunStatusDisplay from './common/PalletizerRunStatusDisplay';
import RemoteSupportIcon from './images/remote_support.svg';
import Dropdown from './common/Dropdown';
import PDFModal from './common/PDFModal';
import { LayoutContent } from './Layout';
import { lWhiteStyle } from './common/Checkbox';
import bell from '../images/bell.png';
const MAX_READ_NOTIFICATIONS = 100;

export interface TabOption {
	Name: string;
	IconClass: string;
	disabled?: boolean;
}

export interface SidebarProps {
	options: TabOption[];
	selected: number;
	setShowConfirmReconnect: (pShow: boolean) => void;
	setShowUserSelect: (pShow: boolean) => void;
	setShowVentionUser: (pShow: boolean) => void;
	showControlCenter?: boolean;
	selectedUserName?: string;
}

interface NotificationLog {
	info: PalletizerInformation;
	seen: boolean;
}

interface RobotNotificationsProps {
	logs: Array<NotificationLog>;
	markAsRead: () => void;
}

export function RobotNotifications({
	logs,
	markAsRead,
}: RobotNotificationsProps) {
	const notificationRef = useRef<HTMLDivElement>(null);
	const [localLogs, setLocalLogs] = useState<NotificationLog[]>(() => [...logs]);
	const [unreadCount, setUnreadCount] = useState<number>(0);
	const [hasError, setHasError] = useState<boolean>(false);
	const [isPdfOpen, setIsPdfOpen] = useState<boolean>(false); // Move state here
	const [currentSearchQuery, setCurrentSearchQuery] = useState<{
		term: string;
		skipToC: boolean;
	}>({
		term: '',
		skipToC: false,
	});

	useEffect(() => {
		setLocalLogs([...logs]);
		const latestLogs = logs.slice(0, 20);
		const newUnreadLogs = latestLogs.filter(log => !log.seen);
		setUnreadCount(newUnreadLogs.length);

		const hasErrorInUnreadLogs = newUnreadLogs.some(
			log => log.info.Type === 'Error'
		);
		setHasError(hasErrorInUnreadLogs);
	}, [logs]);

	const handleDropdownChange = (isOpen: boolean) => {
		if (isOpen) {
			setUnreadCount(0);
			setHasError(false);
		} else {
			setLocalLogs(prevLogs =>
				prevLogs.map(log => ({
					...log,
					seen: true,
				}))
			);
			markAsRead();
		}
	};

	const openPdfViewer = (query: string) => {
		setCurrentSearchQuery({ term: query, skipToC: true });
		setIsPdfOpen(true);
	};

	const latestLogs = logs.slice(0, 20);

	return (
		<>
			{isPdfOpen && (
				<LayoutContent
					pTab={{ Name: ' ', IconClass: ' ' }}
					hideHeader={true}
					modal={true}
				>
					<PDFModal
						onRequestClose={() => setIsPdfOpen(false)}
						pdfUrl="/static/docs/StatusCodes.pdf"
						searchQuery={currentSearchQuery.term}
						skipToC={currentSearchQuery.skipToC}
					/>
				</LayoutContent>
			)}

			<Dropdown
				triggerRef={notificationRef}
				trigger={
					<Button pAction={() => {}} pNormalColorOverride={COLOR.TRANSPARENT}>
						<div className="white white-inner-border flex-center align-center fully-round square height-95">
							<img
								src={bell}
								className="width-60"
								style={{
									filter: lWhiteStyle,
								}}
								alt="Notifications"
							/>
							{unreadCount > 0 && (
								<div
									className={`NotificationDot ${
										hasError
											? 'background-red color-white'
											: 'background-light-blue color-white'
									}`}
									style={{ height: '3vh', width: '3vh', borderRadius: '50%' }}
								>
									<span className="font-3">{unreadCount}</span>
								</div>
							)}
						</div>
					</Button>
				}
				isRightAligned={true}
				displayChangedCallback={handleDropdownChange}
			>
				<div className={`InfoLogDropDownContent vh-${latestLogs.length * 5}`}>
					<Scroll>
						<div>
							{latestLogs.length === 0 ? (
								<span className="flex-row flex-center color-dark-grey">
									Nothing to see here.
								</span>
							) : (
								latestLogs.map((log, index) => (
									<InformationLog key={index} log={log} openPdfViewer={openPdfViewer} />
								))
							)}
						</div>
					</Scroll>
				</div>
			</Dropdown>
		</>
	);
}

(() => {
	const lVersion =
			process.env.REACT_APP_DISPLAY_VERSION || (isWebDeployment() ? 'Web' : 'dev'),
		lCommit = process.env.REACT_APP_DISPLAY_COMMIT || '';
	console.log('APP:', lVersion, lCommit);
})();

export default function Sidebar({
	options,
	selected,
	setShowConfirmReconnect,
	setShowUserSelect,
	setShowVentionUser,
	showControlCenter,
	selectedUserName,
}: SidebarProps) {
	// preferences: icon-cog
	// spin: icon-rdof
	// palletconfig: icon-part
	// general: icon-launch
	/* console.log(process.env); */

	const [isPdfOpen, setIsPdfOpen] = useState<boolean>(false);
	const [initialPage, setInitialPage] = useState<number>(1);

	const lApp = useContext(ApplicationContext) as Application;

	const { lMachineConfig } = useMachineConfig(lApp);

	const IOConfig = useCoreIOConfig();
	const lRuntimeContext = useContext(PalletizerContext) as RuntimeContext;
	const status = lRuntimeContext.operational_state.status;
	const ar_status = lRuntimeContext.ar_status;

	const menuRef = useRef<HTMLDivElement>(null);
	const isFreedriving = status === PalletizerStatus.Freedrive;

	const toggle_teach_mode = function () {
		if (lApp) {
			if (isFreedriving) {
				lApp.end_manual_mode().catch(console.error);
			} else {
				lApp.start_manual_mode().catch(console.error);
			}
		}
	};

	const lVersion =
		process.env.REACT_APP_DISPLAY_VERSION || (isWebDeployment() ? 'Web' : 'dev');

	const [lUnlocking, lSetUnlocking] = useState<boolean>(false);
	const timerRef = useRef<NodeJS.Timeout | null>(null);
	const [clickCount, setClickCount] = useState(0);

	const info_logs = lRuntimeContext.information;
	const [logs, setLogs] = useState<
		Array<{ info: PalletizerInformation; seen: boolean }>
	>([]);
	const [lastPage, setLastPage] = useState<number>(0);

	useEffect(() => {
		// Retrieve previously read notifications from localStorage
		const readNotifications = new Set<string>(
			JSON.parse(localStorage.getItem('readNotifications') || '[]')
		);

		// Map current info_logs to include `seen` state
		// `seen` is true if Description is in readNotifications
		const updatedLogs = info_logs.map(log => ({
			info: log,
			seen: readNotifications.has(log.Description),
		}));
		setLogs(updatedLogs);
	}, [info_logs]);

	const markAsRead = () => {
		setLogs(prevLogs => {
			const allReadDescriptions = prevLogs.map(l => l.info.Description);

			// Retrieve previously read notifications from localStorage
			const storedReadNotifications = JSON.parse(
				localStorage.getItem('readNotifications') || '[]'
			);
			const combinedRead = [...storedReadNotifications, ...allReadDescriptions];

			localStorage.setItem('readNotifications', JSON.stringify(combinedRead));

			// Update the state so all are seen
			return prevLogs.map(log => ({ ...log, seen: true }));
		});
	};

	function toggleLock() {
		if (ar_status === ARStatus.LOCKED) {
			lSetUnlocking(true);
			lApp.stop();
			lApp.unlockLockDoor(ARStatus.UNLOCKED);
			setTimeout(() => {
				lSetUnlocking(false);
			}, 5000);
		} else {
			lApp.unlockLockDoor(ARStatus.LOCKED);
		}
	}

	useEffect(() => {
		if (ar_status === ARStatus.LOCKED) {
			lSetUnlocking(false);
		}
	}, [ar_status]);

	const handleClick = () => {
		setClickCount(prevCount => {
			const newCount = prevCount + 1;

			if (newCount === 5) {
				// Show the Vention user in the select modal after 5 consecutive clicks
				setShowVentionUser(true);
				resetClickCount();
			}

			return newCount;
		});

		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		timerRef.current = setTimeout(() => {
			// Reset the click count after 1 second
			resetClickCount();
		}, 1000);
	};

	const resetClickCount = () => {
		setClickCount(0);
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	};

	const toggleRef = () => {
		if (!!menuRef.current?.click) {
			menuRef.current.click();
		}
	};

	const openUserGuide = () => {
		setInitialPage(1);
		setIsPdfOpen(true);
	};

	const IS_WEB_DEPLOYMENT = isWebDeployment();

	return (
		<section className="flex-row flex-between full-width height-9">
			<div className="full-height flex-row align-center">
				<div
					className="full-height flex-row align-center cursor-pointer"
					onClick={() => {
						window.location.reload();
					}}
					title="MachineApps Palletizer"
				>
					<div className="padding"></div>
					<img
						className="double-margin-left auto-height height-20"
						src={logo}
						alt="Vention Logo"
					/>
					<span className="double-margin-left color-white bold-font font-3">
						V E N T I O N
					</span>
				</div>
				<div className="padding"></div>
				<div className="padding"></div>
				<nav className="full-height flex-row font-5 align-center">
					{options.map((pOption: TabOption, pIndex: number) => {
						return (
							<SidebarNavItem
								{...pOption}
								pIndex={pIndex}
								pIsSelected={selected === pIndex}
								pIsRunning={false}
								key={pIndex}
								pDisabled={
									((status === PalletizerStatus.Running ||
										status === PalletizerStatus.Scanning) &&
										selected !== pIndex) ||
									pOption.disabled
								}
							/>
						);
					})}
				</nav>
			</div>
			{lMachineConfig?.IsMaster ? (
				<div></div>
			) : (
				<div className="full-height flex-row align-center">
					{lMachineConfig?.EnableRemoteSupport && (
						<Button
							pAction={() => {
								const switchTabsEvent = new Event('switchTabs');
								const rootElement = document.querySelector(':root');
								if (rootElement) rootElement.dispatchEvent(switchTabsEvent);
							}}
							className="white-inner-border vh-7 padding-left padding-right"
						>
							<div className="flex-row align-center">
								<div className="flex-row flex-center align-center">
									<RemoteSupportIcon />
								</div>
								<span className="">
									Call Remote
									<br />
									Support
								</span>
							</div>
						</Button>
					)}
					<div className="padding"></div>
					<div className="padding"></div>
					<div className="full-height flex-row flex-center align-center vw-8">
						<PalletizerRunStatusDisplay pWhiteText={true} />
					</div>

					<div className="padding"></div>

					<RobotNotifications logs={logs} markAsRead={markAsRead} />

					<div className="padding"></div>

					<Dropdown
						trigger={
							<Button
								pAction={() => {
									setShowVentionUser(false);
								}}
								pNormalColorOverride={COLOR.TRANSPARENT}
							>
								<div className="white-inner-border background-dark-blue flex-center align-center fully-round square height-95">
									{/* Using new burger_menu.png added in client/src/images 
									TBD Refactor for using SVGs */}
									<img
										src={menu_icon}
										className="width-60"
										alt="Menu Icon"
										style={{
											transform: 'translateX(-1%)',
										}}
									/>
									{/* <span className="color-white bold-font font-5">M</span> */}
								</div>
							</Button>
						}
						isRightAligned={true}
					>
						<>
							<Button
								pDisabled={!showControlCenter || IS_WEB_DEPLOYMENT}
								pAction={() => {
									const switchTabsEvent = new Event('switchTabs');
									const rootElement = document.querySelector(':root');
									if (rootElement) rootElement.dispatchEvent(switchTabsEvent);
								}}
								className="full-width"
							>
								<span className="full-width">Control Center</span>
							</Button>
							<div className="half-padding"></div>
							<Button
								pAction={() => {
									lApp.openClampGripper();
								}}
								className="full-width"
								pDisabled={IS_WEB_DEPLOYMENT}
							>
								<span className="full-width">Release Gripper</span>
							</Button>
							<div className="half-padding"></div>

							<Button
								pAction={() => {
									setShowConfirmReconnect(true);
									handleClick();
								}}
								className="full-width"
							>
								<span className="full-width">Reconnect to Robot</span>
							</Button>
							<div className="half-padding"></div>

							<Button
								pAction={() => {
									setShowUserSelect(true);
								}}
								className="full-width"
							>
								<span className="full-width">{`Select User `}{`(`}{`${selectedUserName}`}{`)`}</span>
							</Button>
							<div className="half-padding"></div>

							<Button
								pAction={() => {
									openUserGuide();
									toggleRef();
								}}
								className="full-width"
								pDisabled={IS_WEB_DEPLOYMENT}
							>
								<span className="full-width">User Guide</span>
							</Button>
							<div className="half-padding"></div>
							<Button
								pAction={() => {
									toggle_teach_mode();
								}}
								className="full-width"
								pDisabled={
									status !== PalletizerStatus.Ready &&
									status !== PalletizerStatus.Freedrive
								}
							>
								<span className="full-width">
									{`${isFreedriving ? 'Disable ' : 'Enable '}Freedrive`}
								</span>
							</Button>
						</>
					</Dropdown>
					{!!getConfigValueFromKey(
						'isPresent',
						getConfigValueFromKey('accessRequest', IOConfig)
					) && (
						<>
							<div className="double-padding"></div>
							<footer className="NavItemWidth flex-center color-white font-normal flex-col">
								<div className="flex-col align-center">
									<Button
										pClass={`AccessRequest${
											lUnlocking
												? 'Busy'
												: ar_status === ARStatus.LOCKED
												? 'Locked'
												: 'Unlocked'
										}`}
										pNormalColorOverride={COLOR.TRANSPARENT}
										pAction={toggleLock}
										pDisabled={true}
									>
										<span className={'icon-lock larger-font padding-left'}></span>
									</Button>
									<span className="font-normal">
										{lUnlocking
											? 'Unlocking...'
											: ar_status === ARStatus.LOCKED
											? 'Locked'
											: 'Unlocked'}
									</span>
								</div>
							</footer>
						</>
					)}
					<div className="double-padding"></div>
					{/* <div className="white-inner-border double-padding rounded" onClick={() => {}}>
					<span className="color-white double-padding">OPERATOR</span>
				</div>
				<div className="padding"></div> */}
				</div>
			)}
			{isPdfOpen && (
				<LayoutContent
					pTab={{ Name: ' ', IconClass: ' ' }}
					hideHeader={true}
					modal={true}
				>
					<PDFModal
						onRequestClose={() => setIsPdfOpen(false)}
						pdfUrl="/static/docs/UserGuide.pdf"
						initialPage={lastPage || initialPage}
						skipToC={false}
						setLastPage={setLastPage}
					/>
				</LayoutContent>
			)}
		</section>
	);
}

interface SidebarNavItemProps extends TabOption {
	pIndex: number;
	pIsSelected: boolean;
	pIsRunning: boolean;
	pDisabled?: boolean;
}

function SidebarNavItem({
	Name,
	IconClass,
	pIndex,
	pIsSelected,
	pIsRunning,
	pDisabled,
}: SidebarNavItemProps) {
	let lLinkDestination = '/' + Name.replaceAll(/\s+/g, ''),
		lClass = 'NavItem hover-dark',
		lEnabled = !pDisabled;

	if (pIsSelected) lClass = 'NavItem is-selected-nav';
	if (!lEnabled) lClass = 'NavItem--disabled';

	const lEnabledCheck = function (e: any) {
		if (lEnabled) return;
		e.preventDefault();
	};

	return (
		<Link
			className={lClass}
			key={pIndex}
			to={lLinkDestination}
			onClick={lEnabledCheck}
		>
			<div
				className={
					'vw-10 flex-center align-center vh-9 flex-col ' +
					(pDisabled ? 'color-dark-grey' : 'color-white')
				}
				title={Name}
			>
				<span className={'NavIcon font-5 bold-font ' + IconClass}></span>
				<span className="bold-font padding-top">{Name}</span>
			</div>
		</Link>
	);
}
